/**
 * Componente de botón de micrófono (click-to-record)
 * Click para iniciar grabación, click para detener y enviar
 */

import { useVoiceRecorder } from '../../hooks/useVoiceRecorder';
import { useAvatarAnimation } from '../../hooks/useAvatarAnimation';
import { useEffect, useRef } from 'react';
import './MicrophoneButton.css';

interface MicrophoneButtonProps {
  onRecordingComplete?: (audioBlob: Blob) => void;
  // Barge-in: el padre interrumpe a Galia (audio + colas) antes de iniciar la
  // grabación, para que el mic solo capture la voz del usuario y no eco del TTS.
  onRecordingStart?: () => void;
  /** Modo profesor (aide-prof): etiquetas en francés. Por defecto español. */
  aideProf?: boolean;
}

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MicrophoneButton({
  onRecordingComplete,
  onRecordingStart,
  aideProf = false
}: MicrophoneButtonProps) {
  const { isRecording, startRecording, stopRecording, recordingSeconds, maxRecordingSeconds } = useVoiceRecorder();
  const { setListening } = useAvatarAnimation();
  const autoStopTriggeredRef = useRef(false);

  useEffect(() => {
    setListening(isRecording);
    if (!isRecording) {
      autoStopTriggeredRef.current = false;
    }
  }, [isRecording, setListening]);

  // Auto-stop al alcanzar el tope para no saturar Gemini STT ni la memoria del
  // browser en monólogos largos de examen (B2/C1/C2). Se dispara una sola vez.
  useEffect(() => {
    if (
      isRecording &&
      recordingSeconds >= maxRecordingSeconds &&
      !autoStopTriggeredRef.current
    ) {
      autoStopTriggeredRef.current = true;
      stopRecording().then((blob) => {
        if (blob && onRecordingComplete) onRecordingComplete(blob);
      });
    }
  }, [isRecording, recordingSeconds, maxRecordingSeconds, stopRecording, onRecordingComplete]);

  const handleClick = async () => {
    try {
      if (!isRecording) {
        // Barge-in: si Galia está hablando, la cortamos antes de abrir el mic.
        // Evita el "queda bloqueada Galia" cuando ambos hablan a la vez (#5).
        if (onRecordingStart) onRecordingStart();
        await startRecording();
      } else {
        const audioBlob = await stopRecording();
        if (audioBlob && onRecordingComplete) {
          onRecordingComplete(audioBlob);
        }
      }
    } catch (error) {
      console.error('Error al manejar grabación:', error);
    }
  };

  const nearLimit = recordingSeconds >= maxRecordingSeconds - 60; // último minuto
  const ariaLabel = aideProf
    ? (isRecording
        ? `Clique pour arrêter et envoyer. Enregistrement ${formatDuration(recordingSeconds)} sur ${formatDuration(maxRecordingSeconds)}.`
        : 'Clique pour enregistrer')
    : (isRecording
        ? `Click para detener y enviar. Grabando ${formatDuration(recordingSeconds)} de ${formatDuration(maxRecordingSeconds)}.`
        : 'Click para grabar');

  return (
    <div className="microphone-button-wrapper">
      <button
        className={`microphone-button ${isRecording ? 'recording' : ''}`}
        onClick={handleClick}
        aria-label={ariaLabel}
      >
        {!isRecording ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mic-icon-svg">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
            <line x1="12" y1="19" x2="12" y2="23"/>
            <line x1="8" y1="23" x2="16" y2="23"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mic-icon-svg recording-icon-svg">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
        )}
      </button>
      {isRecording && (
        <span
          className={`microphone-duration ${nearLimit ? 'near-limit' : ''}`}
          aria-live="polite"
        >
          {formatDuration(recordingSeconds)}
        </span>
      )}
    </div>
  );
}


